<script setup lang="ts">
import UIBanner from '@/shared/ui/Banner';
import UIButton from '@/shared/ui/Button/index';
import UIBreadcrumbs from '@/shared/ui/Breadcrumbs/index';
import Categories from '@/pages/catalog/ui/Categories.vue';
import CategoryCardList from '@/pages/catalog/ui/CategoryCardList.vue';
import type { IBreadcrumbs } from '@/shared/ui/Breadcrumbs/types';
import { useCategoriesStore } from '@/entities/categories/index';
import { useFilterVisibility } from '@/pages/catalog-detail/composables/useFilterVisibility';

const { t } = useI18n();
const localePath = useLocalePath();

const breadcrumbs: Ref<IBreadcrumbs[]> = ref([
  { id: 1, text: t('main'), link: localePath('/') },
  { id: 2, text: t('catalog'), link: '' },
]);

const categoriesStore = useCategoriesStore();
const { isShowFilters, filtersToggleText, toggleFilters } = useFilterVisibility(true);

useSeoMeta({
  title: t('productCategories'),
  ogTitle: t('productCategories'),
  description: t('catalogDescription'),
  ogDescription: t('catalogDescription'),
});
</script>

<template>
  <div :class="['catalog', { _hide: !isShowFilters }]">
    <div class="catalog__breadcrumbs">
      <UIBreadcrumbs :breadcrumbs="breadcrumbs" />
    </div>
    <div class="catalog__button-wrapper">
      <UIButton
        :text="filtersToggleText"
        gray
        :aria-label="filtersToggleText"
        @click="toggleFilters"
      />
    </div>
    <div class="catalog__content">
      <aside v-if="isShowFilters" class="catalog__sidebar">
        <Categories />
      </aside>
      <div class="catalog__main">
        <div class="catalog__banner">
          <UIBanner
            :title="t('dailyServing') + ' <br /> ' + t('beautyAndHealth')"
            :description="t('bestQuality')"
            :images="{
              desktop: '/images/banners/catalog/catalog-banner.png',
              mobile: '/images/banners/catalog/catalog-banner-mobile.png',
            }"
          />
        </div>
        <div class="catalog__products">
          <h1 class="catalog__title">{{ t('productCategories') }}</h1>
          <div class="catalog__products-list">
            <CategoryCardList :categories="categoriesStore.parentCategories" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="less">
.catalog {
  .container-d();
  .flex-column(@size-x6);

  margin-top: @size-x8;
  margin-bottom: 56px;

  &__content {
    display: flex;
    gap: @size-x5;
  }

  &__main {
    .flex-column(@size-x8);

    width: 100%;
    max-width: 1060px;
    margin-top: -67px;
  }

  &__sidebar {
    width: 100%;
    max-width: 340px;
  }

  &__products {
    .flex-column(@size-x6);

    width: 100%;
  }

  &._hide &__main {
    max-width: 100%;
    margin-top: 0;
  }

  &__button-wrapper {
    width: 183px;
  }

  &__banner {
    height: 250px;
  }

  &__title {
    .h2();
  }

  @media @1024 {
    .container-m();

    &__content {
      flex-direction: column;
    }

    &__main {
      margin-top: 0;
    }

    &__sidebar {
      max-width: 100%;
    }

    &__button-wrapper {
      display: none;
    }

    &__banner {
      height: 160px;
    }
  }
}
</style>
